/** API /users/:id returns `{ data: user }`; session may hold the user object directly. */
export function normalizeUserRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  return raw.data !== undefined ? raw.data : raw;
}

/** Company admin role on the session user (same signal as RoleOnly / dashboard). */
export function userHasAdminRole(user) {
  const roles = user?.roles;
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => role?.name === "admin");
}

/**
 * Existing product signal from GET /microsoft/me/status: the user has enabled
 * their own Microsoft/Outlook mailbox and/or can send mail from the profile.
 * Do not invent a parallel flag.
 */
export function userHasPersonalMailbox(microsoftStatus) {
  if (!microsoftStatus || typeof microsoftStatus !== "object") return false;
  return Boolean(
    microsoftStatus.connected ||
      microsoftStatus.can_send_mail ||
      microsoftStatus.email_address
  );
}

/** Banner and signature checklist: admins, or users with a personal mailbox. */
export function userShouldSeeEmailSignatureNag({ isAdmin, microsoftStatus } = {}) {
  return Boolean(isAdmin) || userHasPersonalMailbox(microsoftStatus);
}

/**
 * Required personal details every employee/hired user should fill in so that
 * Promang modules (payroll, HSE, next-of-kin contact, payouts) work optimally.
 * `user_children` is intentionally excluded — it is optional (info only).
 */
export function userProfileIssues(raw) {
  const user = normalizeUserRecord(raw);
  if (!user || typeof user !== "object") return [];

  const issues = [];

  const mobile = [user.mobile, user.mobile_work, user.mobile_phone, user.phone].some(
    (v) => String(v || "").trim().length > 0
  );
  if (!mobile) issues.push("mobile");

  const nextOfKin =
    Array.isArray(user.user_next_of_kins) &&
    user.user_next_of_kins.some((row) => row.active !== false);
  if (!nextOfKin) issues.push("next_of_kin");

  const socialSecurity =
    String(user.social_security || "").trim() ||
    String(user.employment?.social_security || "").trim();
  if (!socialSecurity) issues.push("social_security");

  const accountNumber =
    String(user.account_number || "").trim() ||
    String(user.employment?.account_number || "").trim();
  if (!accountNumber) issues.push("account_number");

  const signature = String(user.email_signature_html || "")
    .replace(/<[^>]+>/g, "")
    .trim();
  if (!signature) issues.push("email_signature");

  return issues;
}

/** True when the user has registered at least one child (optional, info only). */
export function userHasChildren(raw) {
  const user = normalizeUserRecord(raw);
  return Boolean(
    Array.isArray(user?.user_children) &&
      user.user_children.some((row) => row.active !== false)
  );
}

/** Fields needed to evaluate the profile checklist in one /users/:id request. */
export const USER_PROFILE_GUIDE_FIELDS =
  "id,mobile,mobile_work,phone,social_security,account_number,email_signature_html,user_children.id,user_children.active,user_next_of_kins.id,user_next_of_kins.active,employment.social_security,employment.account_number";
